import { Collapse, Grid, IconButton } from '@material-ui/core';
import ExpandLess from '@material-ui/icons/ExpandLess';
import ExpandMore from '@material-ui/icons/ExpandMore';
import copy from 'copy-to-clipboard';
import MaterialTable, { Column } from 'material-table';
import moment from 'moment';
import React from 'react';
import ReactJson, { OnCopyProps } from 'react-json-view';
// @ts-ignore
import XMLViewer from 'react-xml-viewer';

import { IBidResponseUIData } from '~/src/dash/api/live_logs/models';
import { CopyButton } from '~/src/dash/components/button';
import { IStore } from '~/src/dash/reducers/store';
import {
  IBaseState,
  IHistoryProps,
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { decorate } from '~/src/dash/utils/decorator';
import { utils } from '~/src/dash/utils/json';
import { mapBidderType } from '~/src/dash/utils/logs-bid';
import { ct } from '~/src/dash/publicaUI';

interface IRow {
  timestamp: string;
  bidderType: string;
  original_vast: string;
  unwrapped_vast: string;
  original_bid_response: string;
  idx: number;
  original_bid_response_type: string;
  endpoint_request_body: string;
  endpoint_request_body_type: string;
  debugInfo: any;
  recognition_result_live_log_data: string;
}

export interface IProps
  extends IWithStylesProps,
    IReduxActionProps,
    ISessionStateProps,
    IUserStateProps,
    IHistoryProps {
  data: IBidResponseUIData[];
}

interface IState extends IBaseState {
  tableColumns: Array<Column<IRow>>;
  tableData: IRow[];
  activeIndex: number;
}

type Globals = '-moz-initial' | 'inherit' | 'initial' | 'revert' | 'unset';
type WhiteSpaceProperty =
  | Globals
  | '-moz-pre-wrap'
  | 'break-spaces'
  | 'normal'
  | 'nowrap'
  | 'pre'
  | 'pre-line'
  | 'pre-wrap';

function dataToRow(data: IBidResponseUIData[], publisherTimezone: string): IRow[] {
  if (!Array.isArray(data) || data === undefined) {
    return [];
  }

  let i = -10;
  const mappedData = (data || [])
    .map((d: IBidResponseUIData): IRow => {
      i = i + 10;
      // Compute type of original bid response
      let original_bid_response_type = 'xml';
      if (d.original_bid_response !== '' && d.original_bid_response[0] !== '<') {
        original_bid_response_type = 'json';
      }

      // Compute type of original bid response
      let endpoint_request_body_type = 'json';
      try {
        JSON.parse(d.endpoint_request);
      } catch (e) {
        endpoint_request_body_type = '';
      }

      return {
        timestamp:
          moment(d.timestamp).tz(publisherTimezone).format('DD-MM-YYYY[\n]HH:mm:ss') +
          '\n' +
          publisherTimezone,
        bidderType: mapBidderType(d.bidderType),
        original_vast: d.original_vast,
        unwrapped_vast: d.unwrapped_vast,
        original_bid_response: d.original_bid_response,
        original_bid_response_type,
        idx: i,
        endpoint_request_body: d.endpoint_request,
        endpoint_request_body_type,
        debugInfo: d.debugInfo,
        recognition_result_live_log_data: d.recognition_result_live_log_data,
      };
    })
    .sort((a: IRow, b: IRow): number => {
      // Sort data by timestamp desc
      return (
        moment(b.timestamp, 'DD-MM-YYYY HH:mm:ss').unix() -
        moment(a.timestamp, 'DD-MM-YYYY HH:mm:ss').unix()
      );
    });

  return mappedData;
}

function recognitionResultHasData(recognition_result_live_log_data: string): boolean {
  try {
    const recognition_result_live_log_data_json = JSON.parse(recognition_result_live_log_data);

    const hasDetectectedLanguage = recognition_result_live_log_data_json.detected_language !== '';
    const hasAdomain = recognition_result_live_log_data_json.adomain !== '';
    const hasIABCat = recognition_result_live_log_data_json.iab_cat !== '';

    return hasDetectectedLanguage || hasAdomain || hasIABCat;
  } catch {
    return false;
  }
}

export const LogBidResponseTable = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: (theme: any) => ({
      paper: {
        ...theme.mixins.gutters(),
        paddingTop: theme.spacing.unit * 2,
        paddingBottom: theme.spacing.unit * 2,
        marginBottom: theme.spacing.unit * 4,
      },
      section: {
        display: 'block',
        marginBottom: theme.spacing.unit * 4,
      },
      button: {
        margin: theme.spacing.unit,
      },
    }),
    router: true,
  },
  class extends React.Component<IProps, IState> {
    state = {
      tableColumns: [
        {
          title: ct('Timestamp'),
          field: 'timestamp',
          searchable: false,
          width: '40px',
          cellStyle: { whiteSpace: 'pre-wrap' as WhiteSpaceProperty },
        },
        { title: ct('BidderType'), field: 'bidderType', searchable: false },
      ],
      tableData: dataToRow(this.props.data, this.props.session.publisher.Timezone),
      activeIndex: -1,
    };

    handleCopy = (copyObj: OnCopyProps) => {
      copy(JSON.stringify(copyObj.src, null, '\t'));
    };

    handleClick = (index: number) => {
      const { activeIndex } = this.state;
      const newIndex = activeIndex === index ? -1 : index;

      this.setState({ activeIndex: newIndex });
    };

    componentDidUpdate(prevProps: Readonly<IProps>): void {
      if (prevProps.data !== this.props.data) {
        this.setState({
          tableData: dataToRow(this.props.data, this.props.session.publisher.Timezone),
        });
      }
    }

    render() {
      return (
        <div>
          <MaterialTable
            detailPanel={(rowData: IRow) => {
              return (
                <Grid container direction='column'>
                  {/* Original Bid Response */}
                  <Grid item>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item>
                        <IconButton onClick={() => this.handleClick(rowData.idx)}>
                          {this.state.activeIndex === rowData.idx ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <p>{ct('OriginalBidResponse')}</p>
                      </Grid>
                      <Grid item>
                        <CopyButton copy={rowData.original_bid_response} text={ct('Copy')} />
                      </Grid>
                    </Grid>
                    <Collapse in={this.state.activeIndex === rowData.idx}>
                      {rowData.original_bid_response_type === 'json' ? (
                        <ReactJson
                          displayDataTypes={false}
                          src={utils.json.SafeParse(rowData.original_bid_response)}
                          enableClipboard={this.handleCopy}
                        />
                      ) : (
                        <XMLViewer
                          xml={rowData.original_bid_response}
                          invalidXml={<p>{rowData.original_bid_response}</p>}
                        />
                      )}
                    </Collapse>
                  </Grid>
                  {/* Umwrapped Vast*/}
                  <Grid item>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item>
                        <IconButton onClick={() => this.handleClick(rowData.idx + 1)}>
                          {this.state.activeIndex === rowData.idx + 1 ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <p>{ct('UnwrappedVAST')}</p>
                      </Grid>
                      <Grid item>
                        <CopyButton copy={rowData.unwrapped_vast} text={ct('Copy')} />
                      </Grid>
                    </Grid>
                    <Collapse in={this.state.activeIndex === rowData.idx + 1}>
                      <XMLViewer
                        xml={rowData.unwrapped_vast}
                        invalidXml={<p>{rowData.unwrapped_vast}</p>}
                      />
                    </Collapse>
                  </Grid>
                  {/* Vast*/}
                  <Grid item>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item>
                        <IconButton onClick={() => this.handleClick(rowData.idx + 2)}>
                          {this.state.activeIndex === rowData.idx + 2 ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <p>{ct('OriginalVAST')}</p>
                      </Grid>
                      <Grid item>
                        <CopyButton copy={rowData.original_vast} text={ct('Copy')} />
                      </Grid>
                    </Grid>
                    <Collapse in={this.state.activeIndex === rowData.idx + 2}>
                      <XMLViewer
                        xml={rowData.original_vast}
                        invalidXml={<p>{rowData.original_vast}</p>}
                      />
                    </Collapse>
                  </Grid>
                  {/* Endpoint Request */}
                  <Grid item>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item>
                        <IconButton onClick={() => this.handleClick(rowData.idx + 3)}>
                          {this.state.activeIndex === rowData.idx + 3 ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <p>{ct('EndpointRequest')}</p>
                      </Grid>
                      <Grid item>
                        <CopyButton copy={rowData.endpoint_request_body} text={ct('Copy')} />
                      </Grid>
                    </Grid>
                    <Collapse in={this.state.activeIndex === rowData.idx + 3}>
                      {rowData.endpoint_request_body_type === 'json' ? (
                        <ReactJson
                          displayDataTypes={false}
                          src={utils.json.SafeParse(rowData.endpoint_request_body)}
                          enableClipboard={this.handleCopy}
                        />
                      ) : (
                        rowData.endpoint_request_body
                      )}
                    </Collapse>
                  </Grid>
                  {/* Bid Request */}
                  <Grid item>
                    <Grid container direction='row' justify='flex-start' alignItems='center'>
                      <Grid item>
                        <IconButton onClick={() => this.handleClick(rowData.idx + 4)}>
                          {this.state.activeIndex === rowData.idx + 4 ? (
                            <ExpandLess />
                          ) : (
                            <ExpandMore />
                          )}
                        </IconButton>
                      </Grid>
                      <Grid item>
                        <p>{ct('BidRequest')}</p>
                      </Grid>
                      <Grid item>
                        <CopyButton copy={JSON.stringify(rowData.debugInfo)} text={ct('Copy')} />
                      </Grid>
                    </Grid>
                    <Collapse in={this.state.activeIndex === rowData.idx + 4}>
                      {rowData.debugInfo.error !== undefined &&
                      rowData.debugInfo.raw !== undefined ? ( // To handle old logs
                        rowData.debugInfo.raw
                      ) : (
                        <ReactJson
                          displayDataTypes={false}
                          src={rowData.debugInfo}
                          enableClipboard={this.handleCopy}
                        />
                      )}
                    </Collapse>
                  </Grid>
                  {/* Elea AI Recognition Values */}
                  {recognitionResultHasData(rowData.recognition_result_live_log_data) && (
                    <Grid item>
                      <Grid container direction='row' justify='flex-start' alignItems='center'>
                        <Grid item>
                          <IconButton onClick={() => this.handleClick(rowData.idx + 5)}>
                            {this.state.activeIndex === rowData.idx + 5 ? (
                              <ExpandLess />
                            ) : (
                              <ExpandMore />
                            )}
                          </IconButton>
                        </Grid>
                        <Grid item>
                          <p>{ct('EleaAIEnrichment')}</p>
                        </Grid>
                        <Grid item>
                          <CopyButton
                            copy={rowData.recognition_result_live_log_data}
                            text={ct('Copy')}
                          />
                        </Grid>
                        <Collapse in={this.state.activeIndex === rowData.idx + 5}>
                          {
                            <ReactJson
                              displayDataTypes={false}
                              src={utils.json.SafeParse(rowData.recognition_result_live_log_data)}
                              enableClipboard={this.handleCopy}
                            />
                          }
                        </Collapse>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              );
            }}
            style={{
              paddingLeft: '20px',
              paddingRight: '20px',
            }}
            title=''
            options={{
              search: false,
              pageSize: 15,
              pageSizeOptions: [5, 15, 30],
              toolbar: false,
            }}
            columns={this.state.tableColumns}
            data={this.state.tableData}
          />
        </div>
      );
    }
  },
);
